/** The SemanticParser seam: NL question → grounded IR + an explainable trace. */
import type { QueryForm, QueryIR } from "../ir/types.ts";
import type { EntityCandidate, PropertyCandidate } from "../linking/types.ts";

export interface LinkedEntity {
  mention: string;
  chosen?: EntityCandidate;
  alternatives: EntityCandidate[];
}

export interface LinkedRelation {
  phrase: string;
  chosen?: PropertyCandidate;
  alternatives: PropertyCandidate[];
}

/**
 * Everything the UI needs to explain *why* a query was produced: which template
 * fired, which mentions were linked to which IRIs, and the runner-up candidates
 * (the raw material for guided disambiguation later).
 */
export interface ParseTrace {
  question: string;
  normalized: string;
  parser: string;
  template: string;
  form: QueryForm;
  entities: LinkedEntity[];
  relations: LinkedRelation[];
  classConstraint?: { noun: string; qid: string; label: string };
  superlative?: { adjective: string; pid: string; label: string; descending: boolean };
  notes: string[];
}

export interface ParseResult {
  ir: QueryIR;
  trace: ParseTrace;
}

/**
 * Guided-disambiguation pins. When the user clicks an alternative in the Explain
 * panel, the app re-asks with the chosen IRI pinned so grounding uses it instead
 * of the top-scoring candidate.
 */
export interface ParseOverrides {
  /** Pin this entity IRI (a candidate's `.iri`). */
  entityIri?: string;
  entityLabel?: string;
  /** Pin this property IRI (a candidate's `.iri` — the predicate used in a triple). */
  propertyIri?: string;
  propertyLabel?: string;
}

export interface ParseContext {
  signal?: AbortSignal;
  overrides?: ParseOverrides;
}

export interface SemanticParser {
  readonly name: string;
  parse(question: string, ctx?: ParseContext): Promise<ParseResult>;
}

/** Thrown when a question cannot be turned into an IR; carries a partial trace. */
export class ParseError extends Error {
  readonly trace: Partial<ParseTrace>;
  constructor(message: string, trace: Partial<ParseTrace> = {}) {
    super(message);
    this.name = "ParseError";
    this.trace = trace;
  }
}
